// ==============================================
// Login Page (Email/Password)
// ==============================================

import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../providers/AuthProvider';
import { api } from '../../services/api';
import { hardNavigate } from '../../features/crewPapers/navigation';
import { useRevealFocusedField } from '../../lib/viewport/useRevealFocusedField';

/** Seconds before "Send a new code" can be pressed again. */
const RESEND_SECONDS = 30;

/** Server codes whose message is meant for the person, said as it is. */
const SHOWN_CODES = new Set(['BAD_EMAIL', 'BAD_CODE', 'CODE_EXPIRED', 'CODE_USED_UP', 'ACCOUNT_INACTIVE']);
/** A code that cannot work again: the field is cleared for the next one. */
const SPENT_CODES = new Set(['BAD_CODE', 'CODE_EXPIRED', 'CODE_USED_UP']);

type ApiFailure = { response?: { status?: number; data?: { error?: string; code?: string; retryAfterSeconds?: number } } };

/** What to say for a failed login-code call. */
function loginCodeMessage(err: unknown, fallback: string): string {
  const r = (err as ApiFailure)?.response;
  if (r?.status === 429) {
    const s = Number(r.data?.retryAfterSeconds);
    if (!Number.isFinite(s) || s <= 0) return 'Too many tries. Try again later.';
    const minutes = Math.ceil(s / 60);
    return `Too many tries. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
  }
  if (r?.data?.code && SHOWN_CODES.has(r.data.code) && r.data.error) return r.data.error;
  return fallback;
}

/** A path on this site to return to, or null: never one a browser would read as another host. */
function sameSitePath(path: string | null | undefined): string | null {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  return path;
}

export function LoginPage() {
  const { isAuthenticated, isLoading, login, error, clearError } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A login code instead of a password (routes/loginCode.ts on the API): the
  // code is emailed and typed in HERE, so it signs in this screen, a Scout
  // included, and never the phone the email is read on. 'password' keeps this
  // page exactly as it has always been; the other two are only reachable by
  // choosing the code.
  const [mode, setMode] = useState<'password' | 'code-ask' | 'code-enter'>('password');
  const [code, setCode] = useState('');
  // Signing in with a code and sending one are separate, so each button says what is happening.
  const [codeBusy, setCodeBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const busyRef = useRef(false);

  // Keep the field and its button above the Scout's keyboard, on the code screens only.
  const scrollRef = useRef<HTMLDivElement>(null);
  const codeActionRef = useRef<HTMLButtonElement>(null);
  useRevealFocusedField(scrollRef, { companionRef: codeActionRef, enabled: mode !== 'password' });

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  // Get the return URL from state or default to dashboard
  const from = (location.state as { from?: Location })?.from?.pathname || '/dashboard';

  // Cross-origin returnTo (e.g. /api/admin/docs on the API host) — only accept
  // absolute paths to prevent open-redirect via //evil.com or protocol URLs.
  const returnTo = new URLSearchParams(location.search).get('returnTo');
  const safeReturnTo =
    returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : null;

  // If already authenticated, redirect
  if (isAuthenticated && !isLoading) {
    if (safeReturnTo) {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      window.location.assign(`${apiBase}${safeReturnTo}`);
      return null;
    }
    return <Navigate to={from} replace />;
  }

  // The login code. Answered the same whether or not the address has an account.
  const sendLoginCode = async () => {
    if (!email.trim()) {
      setCodeError('Enter your email address first');
      return;
    }
    setSending(true);
    setCodeError(null);
    try {
      await api.sendLoginCode(email.trim());
      setCode('');
      setMode('code-enter');
      setResendIn(RESEND_SECONDS);
    } catch (err) {
      setCodeError(loginCodeMessage(err, 'The code could not be sent. Try again in a moment.'));
    } finally {
      setSending(false);
    }
  };

  // Where a right code goes: a full page load, because the session cookie was
  // set underneath the running app (and on a Scout, the web view sees the new
  // sign-in on that load).
  const afterSignIn = () =>
    safeReturnTo ? `/login?returnTo=${encodeURIComponent(safeReturnTo)}` : sameSitePath(from) ?? '/dashboard';

  const submitCode = async (value: string) => {
    if (value.length !== 6 || busyRef.current) return;
    busyRef.current = true;
    setCodeBusy(true);
    setCodeError(null);
    try {
      await api.verifyLoginCode(email.trim(), value);
      hardNavigate(afterSignIn());
      return; // the page is leaving; stay busy so nothing is sent twice
    } catch (err) {
      const failed = (err as ApiFailure)?.response?.data?.code;
      if (failed && SPENT_CODES.has(failed)) setCode('');
      setCodeError(loginCodeMessage(err, 'Sign-in failed. Try again in a moment.'));
    }
    busyRef.current = false;
    setCodeBusy(false);
  };

  const onCodeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void submitCode(digits);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setIsSubmitting(true);

    try {
      await login(email, password);
      // Redirect will happen automatically via the isAuthenticated check
    } catch {
      // Error is handled by AuthProvider
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Bounded to the normalized usable viewport height: keyboard-aware on the Scout
    // host via ViewportProvider's --vortex-usable-height, and 100vh in ordinary
    // browsers (preserving desktop/iPhone appearance). This element owns vertical
    // scrolling. The inner group centres with auto margins, which yield to
    // top-aligned scrolling when the usable region is shorter than the content, so
    // Sign In stays reachable while the keyboard is open (no top clipping, unlike
    // flex centring). See docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md.
    <div
      ref={scrollRef}
      className="flex overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4"
      style={{ height: 'var(--vortex-usable-height, 100vh)' }}
    >
      <div className="w-full max-w-md m-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/vortex-logo-white.svg" alt="Vortex Marine" className="w-10 h-10" />
            <span className="text-2xl font-bold text-white">Vortex Marine</span>
          </div>
          <p className="text-slate-400">Sign in to access your marine charts</p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 shadow-xl">
          <h1 className="text-2xl font-bold text-white text-center mb-6">
            {mode === 'password' ? 'Welcome Back' : mode === 'code-ask' ? 'Sign in with a login code' : 'Enter your login code'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {mode === 'password' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
              />
            </div>

            {/* Password Input */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs text-cyan-400 hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-10 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50 mt-6"
            >
              {isSubmitting || isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Sign In
                </>
              )}
            </button>
          </form>
          )}

          {/* The only visible change for existing customers: one line, below the
              button, for a login code instead of a password. */}
          {mode === 'password' && (
            <button
              type="button"
              onClick={() => { clearError(); setCodeError(null); setMode('code-ask'); }}
              className="mt-4 w-full text-center text-sm text-cyan-400 hover:underline"
            >
              Email me a login code instead
            </button>
          )}

          {mode === 'code-ask' && (
            <div data-login-code className="space-y-4">
              <p className="text-sm text-slate-400">
                We'll email you a 6-digit code. Type it in here to sign in on this screen.
              </p>
              <div>
                <label htmlFor="code-email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <input
                  id="code-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  placeholder="you@example.com"
                />
              </div>
              {codeError && <p className="text-sm text-red-400">{codeError}</p>}
              <button
                ref={codeActionRef}
                type="button"
                onClick={() => void sendLoginCode()}
                disabled={sending}
                className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send login code'}
              </button>
              <button
                type="button"
                onClick={() => { setCodeError(null); setMode('password'); }}
                className="w-full text-center text-sm text-cyan-400 hover:underline"
              >
                Use my password instead
              </button>
            </div>
          )}

          {mode === 'code-enter' && (
            <div data-login-code className="space-y-4">
              <p className="text-sm text-slate-300">
                {`If ${email.trim()} has a MyPolaris account, we've emailed it a 6-digit code. It expires in 10 minutes.`}
              </p>
              <div>
                <label htmlFor="login-code" className="block text-sm font-medium text-slate-300 mb-2">
                  Login code
                </label>
                <input
                  id="login-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white text-2xl text-center font-mono tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                />
              </div>
              {codeError && <p className="text-sm text-red-400">{codeError}</p>}
              <button
                ref={codeActionRef}
                type="button"
                onClick={() => void submitCode(code)}
                disabled={codeBusy || sending || code.length !== 6}
                className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50"
              >
                {codeBusy ? 'Signing in…' : 'Sign in'}
              </button>
              <button
                type="button"
                onClick={() => void sendLoginCode()}
                disabled={codeBusy || sending || resendIn > 0}
                className="w-full px-4 py-3 bg-transparent border border-slate-600 text-slate-300 rounded-xl font-medium hover:border-slate-500 transition-all disabled:opacity-50"
              >
                {sending ? 'Sending…' : resendIn > 0 ? `Send a new code in ${resendIn} s` : 'Send a new code'}
              </button>
              <button
                type="button"
                onClick={() => { setCode(''); setCodeError(null); setMode('code-ask'); }}
                className="w-full text-center text-sm text-cyan-400 hover:underline"
              >
                Use a different email
              </button>
              <button
                type="button"
                onClick={() => { setCode(''); setCodeError(null); setMode('password'); }}
                className="w-full text-center text-sm text-cyan-400 hover:underline"
              >
                Use my password instead
              </button>
            </div>
          )}

          {/* Terms */}
          <p className="mt-6 text-xs text-center text-slate-500">
            By signing in, you agree to our{' '}
            <a href="/terms" className="text-cyan-400 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</a>
          </p>
        </div>

        {/* Footer Links */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-slate-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-cyan-400 hover:underline font-medium">
              Register
            </Link>
          </p>
          <a href="/" className="text-sm text-slate-500 hover:text-white transition-colors block">
            Back to home
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
